import type {
  Extension,
  Identifier,
  Practitioner,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
  Task,
} from 'fhir/r4';
import { parseFHIRTask } from 'utils/FHIRUtils';

import {
  ADDENDUM_QUESTIONNAIRE_IDS,
  EXT_ORIGINAL_CHART,
  EXT_CHAIN_PARENT,
  EXT_SUBMITTED_FOR_COSIGN,
  PARENT_QR_EXTENSION_URL,
  ID_SYSTEM_LAST_MODIFIED_BY,
  RD_CONSULT_CHART_IDS,
  NPI_SYSTEM,
  ALL_COSIGN_GROUP_IDS,
  ALL_COSIGN_PRACTITIONER_IDS,
} from './constants';

// --- Types ---

type FhirExtension = Extension;

/**
 * A Questionnaire `item` node as these helpers actually receive it.
 *
 * Looser than `fhir4.QuestionnaireItem`: the helpers tolerate nodes served with
 * a missing `linkId`/`type` or an unrecognised `type` (the group/answerable
 * decision falls back on the presence of children), and the traversal helpers
 * tolerate `null` entries in an `item` array.
 *
 * `text`, `answerOption`, `required`, `enableWhen` and `enableBehavior` are not
 * read in this module but are declared because the render sites consuming these
 * nodes (responses.tsx, questionnaire.tsx, questionDiffItem.tsx) read them off
 * the same objects. All optional — the `??` fallbacks at those sites handle
 * absence. `enableWhen` is described only as far as it is read: `question`,
 * `operator`, `answerCoding.code`.
 */
export type LooseQuestionnaireItem = {
  answerOption?: QuestionnaireItemAnswerOption[] | undefined;
  enableBehavior?: string | undefined;
  extension?: Extension[] | undefined;
  enableWhen?:
    | {
        answerCoding?: { code?: string | undefined } | undefined;
        operator?: string | undefined;
        question?: string | undefined;
      }[]
    | undefined;
  item?: (LooseQuestionnaireItem | null)[] | undefined;
  linkId?: string | undefined;
  required?: boolean | undefined;
  text?: string | undefined;
  type?: string | undefined;
};

/**
 * A QuestionnaireResponse `item` node as these helpers actually receive it.
 *
 * Looser than `fhir4.QuestionnaireResponseItem` for the same reason: nodes are
 * walked defensively for `linkId`, `answer` and nested `item`.
 */
export type LooseQuestionnaireResponseItem = {
  answer?: QuestionnaireResponseItemAnswer[] | undefined;
  item?: LooseQuestionnaireResponseItem[] | undefined;
  linkId?: string | undefined;
};

// --- Identifier helpers ---

/** Builds the single-object identifier for a QuestionnaireResponse. */
export function buildIdentifier(practitionerName: string): { system: string; value: string } {
  return { system: ID_SYSTEM_LAST_MODIFIED_BY, value: practitionerName };
}

/**
 * Reads the practitioner name from identifier.
 * Supports single-object and array format for forward-compat.
 */
export function getLastModifiedBy(
  identifier: Identifier | Identifier[] | null | undefined,
): string {
  if (!identifier) return '';
  if (Array.isArray(identifier)) {
    return identifier.find((id) => id.system === ID_SYSTEM_LAST_MODIFIED_BY)?.value ?? '';
  }
  return identifier.value ?? '';
}

/** FHIR QuestionnaireResponse.item.answer allowed value[x] property names. */
const QUESTIONNAIRE_RESPONSE_ANSWER_VALUE_KEYS = [
  'valueBoolean',
  'valueDecimal',
  'valueInteger',
  'valueDate',
  'valueDateTime',
  'valueTime',
  'valueString',
  'valueUri',
  'valueUrl',
  'valueCoding',
  'valueQuantity',
  'valueReference',
  'valueAttachment',
] as const;

/**
 * Removes Questionnaire-only fields (e.g. `initialSelected` on `answerOption`) from a single answer
 * before persisting on a QuestionnaireResponse.
 */
export function sanitizeQuestionnaireResponseAnswer(
  answer: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!answer) return null;
  const sanitized: Record<string, unknown> = {};
  for (const key of QUESTIONNAIRE_RESPONSE_ANSWER_VALUE_KEYS) {
    if (answer[key] !== undefined && answer[key] !== null) {
      sanitized[key] = answer[key];
    }
  }
  return Object.keys(sanitized).length > 0 ? sanitized : null;
}

/**
 * Sanitizes each entry in a QuestionnaireResponse `answer` array.
 *
 * @example
 * ```typescript
 * sanitizeQuestionnaireResponseAnswers([{ initialSelected: true, valueCoding: { code: 'PW-PHONE' } }]);
 * // => [{ valueCoding: { code: 'PW-PHONE' } }]
 * ```
 */
export function sanitizeQuestionnaireResponseAnswers(
  answers: unknown[] | null | undefined,
): Record<string, unknown>[] {
  if (!answers?.length) return [];
  return answers
    .map((a) => sanitizeQuestionnaireResponseAnswer(a as Record<string, unknown>))
    .filter((a): a is Record<string, unknown> => a != null);
}

/**
 * Builds QuestionnaireResponse answers from Questionnaire `answerOption` entries marked `initialSelected`.
 *
 * @remarks
 * Used for contact-preference checkboxes (e.g. PW-FOOD-DLVRY-07, PW-FOOD-DLVRY-08, PW-FOOD-DLVRY-21).
 * Strips Questionnaire-only fields such as `initialSelected` before save.
 *
 * `answerOption` stays `unknown[]`: `fhir-helpers.spec.ts:169` pins the
 * tolerance with a `[null, 'text', 42]` fixture, which a narrower parameter
 * would make illegal while the function still handles it.
 */
export function getInitialSelectedAnswers(
  question: { answerOption?: unknown[] | undefined } | null | undefined,
): Record<string, unknown>[] {
  if (!question?.answerOption?.length) return [];
  const selected = question.answerOption.filter((opt): opt is Record<string, unknown> =>
    Boolean(
      opt &&
      typeof opt === 'object' &&
      (opt as { initialSelected?: boolean }).initialSelected === true,
    ),
  );
  return sanitizeQuestionnaireResponseAnswers(selected);
}

const QUESTIONNAIRE_ANSWERABLE_TYPES = new Set([
  'display',
  'boolean',
  'decimal',
  'integer',
  'date',
  'dateTime',
  'time',
  'string',
  'text',
  'url',
  'choice',
  'open-choice',
  'attachment',
  'reference',
  'quantity',
]);

/** True when a questionnaire node is a structural group (not an answerable question). */
export function isQuestionnaireGroupNode(node: LooseQuestionnaireItem | null | undefined): boolean {
  if (!node?.item?.length) return false;
  if (node.type === 'group') return true;
  if (node.type && QUESTIONNAIRE_ANSWERABLE_TYPES.has(node.type)) return false;
  return true;
}

/**
 * Depth-first search on `QuestionnaireResponse.item` for a short display string for one answer.
 *
 * @remarks
 * Intended for simple text fields (e.g. credentials). Does not format multi-select codings,
 * attachments, or references; use `findQuestionnaireResponseAnswers` for full answer arrays.
 */
export function findQuestionnaireResponseAnswerByLinkId(
  items: LooseQuestionnaireResponseItem[] | undefined,
  linkId: string,
): string | null {
  const answers = findQuestionnaireResponseAnswers(items, linkId);
  if (!answers?.length) return null;
  const a = answers[0]!;
  const raw =
    a.valueString ??
    a.valueCoding?.display ??
    (a.valueDate != null ? String(a.valueDate) : null) ??
    (a.valueDecimal != null ? String(a.valueDecimal) : null) ??
    (a.valueInteger != null ? String(a.valueInteger) : null);
  if (raw != null) {
    const s = String(raw).trim();
    if (s !== '') return s;
  }
  return null;
}

/**
 * Flattens a questionnaire page's `item` tree into answerable leaf questions (skips group containers).
 */
export function flattenQuestionnaireItems(
  items: (LooseQuestionnaireItem | null)[] | undefined,
): LooseQuestionnaireItem[] {
  const acc: LooseQuestionnaireItem[] = [];
  for (const node of items ?? []) {
    if (!node) continue;
    if (isQuestionnaireGroupNode(node)) {
      acc.push(...flattenQuestionnaireItems(node.item));
    } else {
      acc.push(node);
    }
  }
  return acc;
}

/** One flattened question list per top-level questionnaire page/group. */
export function getQuestionnairePageItems(
  questionnaire: { item?: (LooseQuestionnaireItem | null)[] } | undefined,
): LooseQuestionnaireItem[][] {
  return (questionnaire?.item ?? []).map((page) => flattenQuestionnaireItems(page?.item));
}

export type QuestionnaireItemCoords = { pageIndex: number; questionIndex: number };

/** Maps question `linkId` to `[pageIndex, questionIndex]` in flattened page layout. */
export function buildQuestionnaireLinkIdIndex(
  pageItems: LooseQuestionnaireItem[][],
): Map<string, QuestionnaireItemCoords> {
  const index = new Map<string, QuestionnaireItemCoords>();
  pageItems.forEach((questions, pageIndex) => {
    questions.forEach((question, questionIndex) => {
      if (question?.linkId && !index.has(question.linkId)) {
        index.set(question.linkId, { pageIndex, questionIndex });
      }
    });
  });
  return index;
}

/**
 * Indexes all answers in a QuestionnaireResponse tree by question `linkId` (first match wins).
 */
export function indexQuestionnaireResponseAnswers(
  items: LooseQuestionnaireResponseItem[] | undefined,
): Map<string, QuestionnaireResponseItemAnswer[]> {
  const index = new Map<string, QuestionnaireResponseItemAnswer[]>();
  const walk = (nodes: LooseQuestionnaireResponseItem[] | undefined) => {
    for (const node of nodes ?? []) {
      if (node.linkId && node.answer?.length && !index.has(node.linkId)) {
        index.set(node.linkId, node.answer);
      }
      if (node.item?.length) walk(node.item);
    }
  };
  walk(items);
  return index;
}

/**
 * Depth-first search for a QuestionnaireResponse answer array by question linkId.
 */
export function findQuestionnaireResponseAnswers(
  items: LooseQuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItemAnswer[] | null {
  if (!items?.length) return null;
  for (const node of items) {
    if (node.linkId === linkId && node.answer?.length) {
      return node.answer;
    }
    const nested = findQuestionnaireResponseAnswers(node.item, linkId);
    if (nested) return nested;
  }
  return null;
}

// --- Extension readers ---

/** Reads the original-chart QR ID from extensions. */
export function getOriginalChartId(extensions?: FhirExtension[]): string | null {
  return extensions?.find((e) => e.url === EXT_ORIGINAL_CHART)?.valueString ?? null;
}

/** Reads the chain-parent QR ID from extensions. */
export function getChainParentId(extensions?: FhirExtension[]): string | null {
  return extensions?.find((e) => e.url === EXT_CHAIN_PARENT)?.valueString ?? null;
}

/**
 * Backward-compat: reads the parent QR ID from the new original-chart
 * extension, falling back to the legacy parent-questionnaire-response extension.
 */
export function getParentResponseId(extensions?: FhirExtension[]): string | null {
  const fromNew = getOriginalChartId(extensions);
  if (fromNew) return fromNew;
  return extensions?.find((e) => e.url === PARENT_QR_EXTENSION_URL)?.valueString ?? null;
}

// --- Extension builders ---

/** Appends original-chart and chain-parent extensions to an extension array. */
export function appendAddendumExtensions(
  extensions: FhirExtension[],
  originalChartId: string,
  chainParentId: string,
): void {
  extensions.push({ url: EXT_ORIGINAL_CHART, valueString: originalChartId });
  extensions.push({ url: EXT_CHAIN_PARENT, valueString: chainParentId });
}

/** Checks whether a QR was explicitly submitted for co-sign (not just a draft). */
export function isSubmittedForCosign(extensions?: FhirExtension[]): boolean {
  if (!extensions) return false;
  return extensions.some((e) => e.url === EXT_SUBMITTED_FOR_COSIGN && e.valueString === 'true');
}

/** Type-safe check for whether a questionnaire ID is an addendum variant. */
export function isAddendumQuestionnaire(id: string): boolean {
  return ADDENDUM_QUESTIONNAIRE_IDS.includes(id);
}

/** Type-safe check for whether a questionnaire ID is an RD consult chart variant. */
export function isRdConsultChart(id: string): boolean {
  return RD_CONSULT_CHART_IDS.includes(id);
}

/**
 * Checks if a questionnaire definition has the updateAfterCompletion readOnly
 * extension, meaning completed responses should not be edited/duplicated/deleted.
 */
export function isReadOnlyAfterCompletion(questionnaireExtension?: FhirExtension[]): boolean {
  if (!questionnaireExtension) return false;
  return questionnaireExtension.some(
    (e) =>
      e.url === 'https://projectwell.io/fhir/StructureDefinition/updateAfterCompletion' &&
      e.valueString === 'readOnly',
  );
}

// --- Co-Sign helpers ---

/** Checks whether a practitioner resource carries an NPI identifier. */
export function hasNpiIdentifier(
  practitionerResource: { identifier?: Identifier[] | undefined } | null | undefined,
): boolean {
  if (!practitionerResource?.identifier) return false;
  return practitionerResource.identifier.some((id) => id.system === NPI_SYSTEM);
}

/**
 * Filters practitioner bundle entries to those with an NPI identifier.
 * Returns dropdown-ready objects with id, fullName, and npi value.
 */
export function getNpiPractitioners(
  practitionerData: { resource?: Practitioner }[],
): { id: string; fullName: string; npi: string }[] {
  if (!practitionerData) return [];
  return practitionerData
    .filter((entry) => hasNpiIdentifier(entry.resource))
    .map((entry) => {
      // Asserted, not guarded: `hasNpiIdentifier` above only passes entries whose
      // resource carries an identifier, and one that reached here without must throw.
      const r = entry.resource!;
      const name = r.name?.[0];
      const given = name?.given?.join(' ') ?? '';
      const family = name?.family ?? '';
      const fullName = name?.text ?? `${given} ${family}`.trim();
      const npi = r.identifier!.find((id) => id.system === NPI_SYSTEM)?.value ?? '';
      return { id: r.id!, fullName, npi };
    });
}

/**
 * Determines whether a QuestionnaireResponse is awaiting co-sign by checking
 * if any co-sign practitioner field (chart or addendum) has a value.
 * Callers should gate on status === 'amended' so that completed QRs are not treated as awaiting.
 */
export function isAwaitingCosign(qrItems: unknown[] | null | undefined): boolean {
  if (!qrItems) return false;
  // Cast, not a narrowing. Upstream reads `group.linkId` off each element unguarded, so a null or
  // non-object element throws; a `continue` guard here would SKIP it instead, which survives type
  // erasure and would be a runtime change. The cast emits nothing.
  for (const group of qrItems as LooseQuestionnaireResponseItem[]) {
    if (ALL_COSIGN_GROUP_IDS.includes(group.linkId!) && group.item) {
      for (const item of group.item) {
        if (ALL_COSIGN_PRACTITIONER_IDS.includes(item.linkId!) && (item.answer?.length ?? 0) > 0) {
          return true;
        }
      }
    }
  }
  return false;
}

/** Extracts the assigned co-sign practitioner ID from a QR's items. */
export function getCosignPractitionerId(qrItems: unknown[] | null | undefined): string | null {
  if (!qrItems) return null;
  // Cast, not a narrowing — see `isAwaitingCosign` above.
  for (const group of qrItems as LooseQuestionnaireResponseItem[]) {
    if (ALL_COSIGN_GROUP_IDS.includes(group.linkId!) && group.item) {
      for (const item of group.item) {
        // The answer read stays INSIDE the condition, after the linkId test, as upstream has it.
        // Hoisting it above evaluates it for every item rather than only matching ones, which
        // reverses upstream's short-circuit.
        if (ALL_COSIGN_PRACTITIONER_IDS.includes(item.linkId!) && item.answer?.[0]?.valueString) {
          return item.answer[0].valueString;
        }
      }
    }
  }
  return null;
}

/** Checks if a questionnaire ID requires co-sign logic (RD consult chart or addendum). */
export function requiresCosignWorkflow(questionnaireId: string): boolean {
  return isRdConsultChart(questionnaireId) || isAddendumQuestionnaire(questionnaireId);
}

/**
 * Retrieves the full sequence of linked QuestionnaireResponses (original chart + all addenda)
 * for a given response ID. Resolves the root chart and traverses the chain.
 *
 * @param qrId Any QuestionnaireResponse ID (can be the original chart or an addendum)
 * @param allResponses The list of all available QuestionnaireResponse entries or resources
 * @returns An array of linked responses, starting with the original chart, followed by addenda in chain order.
 */
export function getLinkedQuestionnaireResponseChain<
  T extends { resource?: QuestionnaireResponse } | QuestionnaireResponse,
>(qrId: string, allResponses: T[]): T[] {
  if (!allResponses || !Array.isArray(allResponses)) return [];

  /**
   * Accepts either a bundle entry or a bare resource — both call sites in
   * `responses.tsx` pass entries, but the doc comment above promises both, and
   * the `||` (not `??`) means an entry whose `resource` is falsy falls through
   * to the entry itself.
   */
  const getRes = (entry: T): QuestionnaireResponse =>
    ((entry as { resource?: QuestionnaireResponse }).resource || entry) as QuestionnaireResponse;

  const targetEntry = allResponses.find((e) => getRes(e).id === qrId);
  if (!targetEntry) return [];

  const targetRes = getRes(targetEntry);
  const originalChartId =
    getOriginalChartId(targetRes.extension) ||
    getParentResponseId(targetRes.extension) ||
    targetRes.id;

  const validResponses = allResponses.filter((e) => getRes(e).status !== 'entered-in-error');

  const originalChartEntry = validResponses.find((e) => getRes(e).id === originalChartId);

  const linkedAddenda = validResponses.filter((e) => {
    const r = getRes(e);
    return (
      r.id !== originalChartId &&
      (getOriginalChartId(r.extension) === originalChartId ||
        getParentResponseId(r.extension) === originalChartId)
    );
  });

  const chain: T[] = [];
  // `QuestionnaireResponse.id` is `0..1`, so an id-less response contributes
  // `undefined` here and every later `addedIds.has(undefined)` matches it —
  // collapsing all id-less responses onto one chain slot, the same way the
  // `QRDataObj` map does.
  const addedIds = new Set<string | undefined>();

  if (originalChartEntry) {
    chain.push(originalChartEntry);
    addedIds.add(originalChartId);
  }

  const addendaByChainParent = new Map<string, T[]>();
  linkedAddenda.forEach((entry) => {
    const r = getRes(entry);
    const parentId = getChainParentId(r.extension);
    if (parentId) {
      if (!addendaByChainParent.has(parentId)) addendaByChainParent.set(parentId, []);
      addendaByChainParent.get(parentId)!.push(entry);
    }
  });

  let currentParentIds = [originalChartId];
  while (currentParentIds.length > 0) {
    // `id` is `0..1`, so an id-less child pushes `undefined` here and the map
    // lookup on the next pass misses — the chain simply stops at that link.
    const nextParentIds: (string | undefined)[] = [];
    for (const pid of currentParentIds) {
      const children = addendaByChainParent.get(pid!) || [];
      children.sort(
        (a, b) =>
          new Date(getRes(a).authored || 0).getTime() - new Date(getRes(b).authored || 0).getTime(),
      );

      for (const child of children) {
        const childId = getRes(child).id;
        if (!addedIds.has(childId)) {
          chain.push(child);
          addedIds.add(childId);
          nextParentIds.push(childId);
        }
      }
    }
    currentParentIds = nextParentIds;
  }

  const remaining = linkedAddenda.filter((a) => !addedIds.has(getRes(a).id));
  remaining.sort(
    (a, b) =>
      new Date(getRes(a).authored || 0).getTime() - new Date(getRes(b).authored || 0).getTime(),
  );

  return [...chain, ...remaining];
}

/**
 * Resolves a display label for PW-OTHER-13 answers (Task id in `valueString`) from a patient Task search bundle.
 * Uses the same `reasonCode` + due date format as the consult task dropdown in `types/string.tsx`.
 *
 * @param taskId - Task id stored on the QuestionnaireResponse answer
 * @param tasksData - FHIR Bundle from e.g. `GET /Task?patient=...`
 * @returns Label string, or `null` if the task is not in the bundle or cannot be parsed
 */
export function getConsultationTaskLabelFromBundle(
  taskId: string | undefined,
  tasksData: { entry?: Array<{ resource?: Task }> } | null | undefined,
): string | null {
  if (!taskId || !tasksData?.entry?.length) return null;
  const entry = tasksData.entry.find((e) => e?.resource?.id === taskId);
  if (!entry?.resource || entry.resource.resourceType !== 'Task') return null;
  const task = parseFHIRTask(entry.resource);
  if (task?.reasonCode && task?.dueDate) {
    return `${task.reasonCode}-${task.dueDate}`;
  }
  return task?.description || null;
}
